import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { SimpleNativeSelect } from './ui/NativeSelect';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, BarChart3, Calculator, FileText, Info, Trash2, TrendingUp } from 'lucide-react';

export const SalaryAdjustment = ({ employees, formatCurrency, onClose }) => {
  const [salaryAdjustmentForm, setSalaryAdjustmentForm] = useState({
    employee_id: "",
    target_net_salary: 0,
    prime_transport: 0,
    avantages_nature: 0,
    avantages_argent: 0,
    avances: 0,
    anciennete: 0,
    indemnite_frais_emploi: 0,
    remboursement_frais: 0,
    prime_panier: 0,
    prime_salissure: 0,
    prime_tenue: 0,
    prime_outillage: 0,
    minimum_net_display: null
  });
  const [salaryAdjustmentResult, setSalaryAdjustmentResult] = useState(null);

  // Callback pour la sélection d'employé dans salary adjustment
  const handleSalaryAdjustmentEmployeeChange = useCallback((value) => {
    try {
      const selectedEmployee = employees.find(emp => emp.id === value);
      
      if (selectedEmployee) {
        // Pré-remplir TOUS les éléments de paie depuis la fiche employé
        const updatedForm = {
          ...salaryAdjustmentForm,
          employee_id: value,
          // Éléments additionnels de rémunération
          prime_transport: selectedEmployee.prime_transport || 0,
          avantages_nature: selectedEmployee.avantages_nature || 0,
          avantages_argent: selectedEmployee.avantages_argent || 0,
          avances: 0, // Les avances ne sont pas stockées dans l'employé
          // Indemnités fiscales
          anciennete: 0, // La prime d'ancienneté du profil est déjà appliquée automatiquement par le moteur
          indemnite_frais_emploi: selectedEmployee.indemnite_frais_emploi || 0,
          remboursement_frais: selectedEmployee.remboursement_frais || 0,
          // Primes spéciales exonérées plafonnées
          prime_panier: selectedEmployee.prime_panier || 0,
          prime_salissure: selectedEmployee.prime_salissure || 0,
          prime_tenue: selectedEmployee.prime_tenue || 0,
          prime_outillage: selectedEmployee.prime_outillage || 0,
          minimum_net_display: null
        };
        
        setSalaryAdjustmentForm(updatedForm);
        
        // Salaire net minimum calculé par le moteur de paie réel (backend)
        axios.post(`${API}/payroll/adjust-sursalaire`, {
          employee_id: value,
          target_net_salary: 0,
          prime_transport: updatedForm.prime_transport,
          avantages_nature: updatedForm.avantages_nature,
          avantages_argent: updatedForm.avantages_argent,
          avances: 0,
          anciennete: 0,
          indemnite_frais_emploi: updatedForm.indemnite_frais_emploi,
          remboursement_frais: updatedForm.remboursement_frais,
          prime_panier: updatedForm.prime_panier,
          prime_salissure: updatedForm.prime_salissure,
          prime_tenue: updatedForm.prime_tenue,
          prime_outillage: updatedForm.prime_outillage
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
          .then(res => {
            setSalaryAdjustmentForm(prev => ({ ...prev, minimum_net_display: res.data.minimum_net }));
          })
          .catch(err => console.error('Erreur calcul net minimum:', err));
        
        toast.success('Informations de l\'employé chargées automatiquement');
      } else {
        setSalaryAdjustmentForm(prev => ({ ...prev, employee_id: value }));
      }
    } catch (error) {
      console.error('Erreur sélection employé salary adjustment:', error);
      toast.error('Erreur lors du chargement des informations de l\'employé');
    }
  }, [employees, salaryAdjustmentForm]);

  // Fonction de calcul automatique du sursalaire
  const handleSalaryAdjustment = async (e) => {
    e.preventDefault();
    
    console.log('🔍 handleSalaryAdjustment démarré');
    console.log('📋 salaryAdjustmentForm:', salaryAdjustmentForm);
    
    if (!salaryAdjustmentForm.employee_id) {
      toast.error("Veuillez sélectionner un employé");
      return;
    }

    if (!salaryAdjustmentForm.target_net_salary || salaryAdjustmentForm.target_net_salary <= 0) {
      toast.error("Veuillez saisir un salaire net cible valide");
      return;
    }

    try {
      // Calcul du sursalaire via le moteur de paie réel (backend)
      const payload = {
        employee_id: salaryAdjustmentForm.employee_id,
        target_net_salary: parseFloat(salaryAdjustmentForm.target_net_salary) || 0,
        prime_transport: parseFloat(salaryAdjustmentForm.prime_transport) || 0,
        avantages_nature: parseFloat(salaryAdjustmentForm.avantages_nature) || 0,
        avantages_argent: parseFloat(salaryAdjustmentForm.avantages_argent) || 0,
        avances: parseFloat(salaryAdjustmentForm.avances) || 0,
        anciennete: parseFloat(salaryAdjustmentForm.anciennete) || 0,
        indemnite_frais_emploi: parseFloat(salaryAdjustmentForm.indemnite_frais_emploi) || 0,
        remboursement_frais: parseFloat(salaryAdjustmentForm.remboursement_frais) || 0,
        prime_panier: parseFloat(salaryAdjustmentForm.prime_panier) || 0,
        prime_salissure: parseFloat(salaryAdjustmentForm.prime_salissure) || 0,
        prime_tenue: parseFloat(salaryAdjustmentForm.prime_tenue) || 0,
        prime_outillage: parseFloat(salaryAdjustmentForm.prime_outillage) || 0
      };
      
      const response = await axios.post(`${API}/payroll/adjust-sursalaire`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const result = response.data;
      
      if (result.feasible === false) {
        setSalaryAdjustmentForm(prev => ({ ...prev, minimum_net_display: result.minimum_net }));
        toast.error(
          result.detail || `Salaire net cible trop bas ! Le salaire net minimum possible pour cet employé est de ${formatCurrency(result.minimum_net)}.`,
          { duration: 5000 }
        );
        return;
      }
      
      setSalaryAdjustmentForm(prev => ({ ...prev, minimum_net_display: result.minimum_net }));
      setSalaryAdjustmentResult(result);
      toast.success("Calcul du sursalaire effectué avec succès");
      
    } catch (error) {
      console.error('❌ Erreur lors du calcul du sursalaire:', error);
      toast.error(`Erreur lors du calcul du sursalaire: ${error.response?.data?.detail || error.message}`);
    }
  };

  return (
                      <div style={{
                        backgroundColor: '#f9fafb',
                        minHeight: '100vh',
                        width: '100%'
                      }}>
                            {/* Header avec gradient */}
                            <div style={{ 
                              padding: '24px 32px',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              position: 'relative'
                            }}>
                              <div style={{ 
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '300px',
                                height: '100%',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                                pointerEvents: 'none'
                              }} />
                              
                              <Button
                                onClick={() => onClose()}
                                variant="outline"
                                style={{ 
                                  marginBottom: '16px',
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  borderColor: 'rgba(255,255,255,0.3)',
                                  color: 'white',
                                  backdropFilter: 'blur(10px)'
                                }}
                              >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Retour
                              </Button>
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ 
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  borderRadius: '12px',
                                  padding: '12px',
                                  backdropFilter: 'blur(10px)'
                                }}>
                                  <TrendingUp style={{ width: '32px', height: '32px' }} />
                                </div>
                                <div>
                                  <h1 style={{ 
                                    fontSize: '28px', 
                                    fontWeight: 700,
                                    marginBottom: '4px',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                    Ajustement automatique du sursalaire
                                  </h1>
                                  <p style={{ fontSize: '14px', opacity: 0.95 }}>
                                    Calculer automatiquement le sursalaire nécessaire pour atteindre un salaire net cible
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div style={{ padding: '32px', backgroundColor: '#f9fafb', maxWidth: '1200px', margin: '0 auto' }}>
                              {/* Formulaire */}
                              <div style={{
                                backgroundColor: 'white',
                                borderRadius: '16px',
                                padding: '32px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e5e7eb'
                              }}>
                                <div style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  marginBottom: '24px',
                                  paddingBottom: '16px',
                                  borderBottom: '2px solid #10b981'
                                }}>
                                  <div style={{
                                    backgroundColor: '#d1fae5',
                                    borderRadius: '10px',
                                    padding: '10px',
                                    color: '#059669'
                                  }}>
                                    <Calculator style={{ width: '24px', height: '24px' }} />
                                  </div>
                                  <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827' }}>
                                    Informations du calcul
                                  </h2>
                                </div>
                                <form id="salary-adjustment-form" onSubmit={handleSalaryAdjustment} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {/* Sélection employé et salaire cible - Carte */}
                                    <div style={{
                                      backgroundColor: '#f0fdf4',
                                      borderRadius: '12px',
                                      padding: '20px',
                                      border: '2px solid #bbf7d0'
                                    }}>
                                      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#166534', marginBottom: '16px' }}>
                                        👤 Employé et objectif
                                      </h3>
                                      <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <Label>Employé</Label>
                                        <SimpleNativeSelect
                                          value={salaryAdjustmentForm.employee_id} 
                                          onValueChange={handleSalaryAdjustmentEmployeeChange}
                                          placeholder="Sélectionner un employé"
                                          options={employees.map((employee) => ({
                                            value: employee.id,
                                            label: `${employee.first_name} ${employee.last_name} (${formatCurrency(employee.salary_categoriel || 0)})`
                                          }))}
                                        />
                                        {salaryAdjustmentForm.minimum_net_display && (
                                          <p className="text-xs text-orange-600 mt-1">
                                            💡 Salaire net minimum: {formatCurrency(salaryAdjustmentForm.minimum_net_display)}
                                          </p>
                                        )}
                                      </div>
                                      <div>
                                        <Label>Salaire net cible (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          placeholder="Ex: 105000"
                                          value={salaryAdjustmentForm.target_net_salary} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, target_net_salary: parseFloat(e.target.value) || 0})}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px'
                                          }}
                                        />
                                        {salaryAdjustmentForm.minimum_net_display && salaryAdjustmentForm.target_net_salary > 0 && salaryAdjustmentForm.target_net_salary < salaryAdjustmentForm.minimum_net_display && (
                                          <p className="text-xs text-red-600 mt-1">
                                            ⚠️ Cible trop basse ! Minimum: {formatCurrency(salaryAdjustmentForm.minimum_net_display)}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    </div>

                                    {/* Éléments additionnels - Carte */}
                                    <div style={{
                                      backgroundColor: '#fef3c7',
                                      borderRadius: '12px',
                                      padding: '20px',
                                      border: '2px solid #fde68a'
                                    }}>
                                      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#92400e', marginBottom: '16px' }}>
                                        💰 Éléments additionnels de rémunération
                                      </h3>
                                      <div className="grid grid-cols-3 gap-4">
                                      <div>
                                        <Label>Prime transport (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          value={salaryAdjustmentForm.prime_transport} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, prime_transport: parseFloat(e.target.value) || 0})}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px'
                                          }}
                                        />
                                      </div>
                                      <div>
                                        <Label>Avantages nature (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          value={salaryAdjustmentForm.avantages_nature} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, avantages_nature: parseFloat(e.target.value) || 0})}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px'
                                          }}
                                        />
                                      </div>
                                      <div>
                                        <Label>Avantages en argent (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          data-testid="adjustment-avantages-argent-input"
                                          value={salaryAdjustmentForm.avantages_argent} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, avantages_argent: parseFloat(e.target.value) || 0})}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px'
                                          }}
                                        />
                                      </div>
                                      <div>
                                        <Label>Avances (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          value={salaryAdjustmentForm.avances} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, avances: parseFloat(e.target.value) || 0})}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px'
                                          }}
                                        />
                                      </div>
                                    </div>
                                    </div>

                                    {/* Nouveaux champs additionnels - Carte */}
                                    <div style={{
                                      backgroundColor: '#ede9fe',
                                      borderRadius: '12px',
                                      padding: '20px',
                                      border: '2px solid #ddd6fe'
                                    }}>
                                      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#5b21b6', marginBottom: '16px' }}>
                                        📊 Indemnités fiscales
                                      </h3>
                                      <div className="grid grid-cols-3 gap-4">
                                      <div>
                                        <Label>Ancienneté (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          value={salaryAdjustmentForm.anciennete} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, anciennete: parseFloat(e.target.value) || 0})}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px'
                                          }}
                                          placeholder="0"
                                        />
                                      </div>
                                      <div>
                                        <Label>Indemnité frais d'emploi (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          value={salaryAdjustmentForm.indemnite_frais_emploi} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, indemnite_frais_emploi: parseFloat(e.target.value) || 0})}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px'
                                          }}
                                          placeholder="0"
                                        />
                                      </div>
                                      <div>
                                        <Label>Remboursement de frais (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          value={salaryAdjustmentForm.remboursement_frais} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, remboursement_frais: parseFloat(e.target.value) || 0})}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px'
                                          }}
                                          placeholder="0"
                                        />
                                      </div>
                                    </div>
                                    </div>
                                    
                                    {/* Primes spéciales exonérées - Carte */}
                                    <div style={{
                                      backgroundColor: '#dcfce7',
                                      borderRadius: '12px',
                                      padding: '20px',
                                      border: '2px solid #bbf7d0'
                                    }}>
                                      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#166534', marginBottom: '16px' }}>
                                        ✅ Primes spéciales exonérées (plafonnées SMIG)
                                      </h3>
                                      <div className="grid grid-cols-4 gap-4">
                                      <div>
                                        <Label>Prime de panier (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          data-testid="adjustment-prime-panier-input"
                                          value={salaryAdjustmentForm.prime_panier} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, prime_panier: parseFloat(e.target.value) || 0})}
                                          style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                          placeholder="0"
                                        />
                                      </div>
                                      <div>
                                        <Label>Prime de salissure (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          data-testid="adjustment-prime-salissure-input"
                                          value={salaryAdjustmentForm.prime_salissure} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, prime_salissure: parseFloat(e.target.value) || 0})}
                                          style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                          placeholder="0"
                                        />
                                      </div>
                                      <div>
                                        <Label>Tenue de travail (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          data-testid="adjustment-prime-tenue-input"
                                          value={salaryAdjustmentForm.prime_tenue} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, prime_tenue: parseFloat(e.target.value) || 0})}
                                          style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                          placeholder="0"
                                        />
                                      </div>
                                      <div>
                                        <Label>Prime d'outillage (FCFA)</Label>
                                        <Input 
                                          type="number" 
                                          data-testid="adjustment-prime-outillage-input"
                                          value={salaryAdjustmentForm.prime_outillage} 
                                          onChange={(e) => setSalaryAdjustmentForm({...salaryAdjustmentForm, prime_outillage: parseFloat(e.target.value) || 0})}
                                          style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                          placeholder="0"
                                        />
                                      </div>
                                    </div>
                                    <p style={{ fontSize: '12px', color: '#166534', marginTop: '12px' }}>
                                      💡 Ces valeurs sont pré-remplies depuis la fiche employé, ainsi que la prime d'ancienneté, la gratification, les allocations familiales et les allocations spéciales (Art. 116-1), toutes prises en compte automatiquement par le moteur de paie.
                                    </p>
                                    </div>
                                    
                                    {/* Boutons d'action */}
                                    <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                                      <Button 
                                        type="submit" 
                                        style={{ flex: 1 }}
                                        disabled={!salaryAdjustmentForm.employee_id || !salaryAdjustmentForm.target_net_salary}
                                      >
                                        Calculer le sursalaire nécessaire
                                      </Button>
                                      
                                      <Button 
                                        type="button"
                                        variant="destructive"
                                        onClick={() => {
                                          if (window.confirm('Voulez-vous réinitialiser le formulaire ?')) {
                                            setSalaryAdjustmentResult(null);
                                            setSalaryAdjustmentForm({
                                              employee_id: "",
                                              target_net_salary: 0,
                                              month: new Date().getMonth() + 1,
                                              year: new Date().getFullYear(),
                                              prime_transport: 0,
                                              avantages_nature: 0,
                                              avantages_argent: 0,
                                              avances: 0,
                                              anciennete: 0,
                                              indemnite_frais_emploi: 0,
                                              remboursement_frais: 0,
                                              prime_panier: 0,
                                              prime_salissure: 0,
                                              prime_tenue: 0,
                                              prime_outillage: 0,
                                              minimum_net_display: null
                                            });
                                            toast.success('Formulaire réinitialisé');
                                          }
                                        }}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Réinitialiser
                                      </Button>
                                    </div>

                                    {/* Résultat du calcul */}
                                    {salaryAdjustmentResult && (
                                      <div style={{
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
                                        border: '3px solid #10b981'
                                      }}>
                                        {/* En-tête du résultat */}
                                        <div style={{
                                          background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                          padding: '24px',
                                          borderBottom: '2px solid #10b981'
                                        }}>
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            marginBottom: '16px'
                                          }}>
                                            <div style={{
                                              backgroundColor: '#10b981',
                                              borderRadius: '10px',
                                              padding: '10px',
                                              color: 'white'
                                            }}>
                                              <BarChart3 style={{ width: '24px', height: '24px' }} />
                                            </div>
                                            <h4 style={{ fontSize: '20px', fontWeight: 700, color: '#065f46' }}>
                                              Résultat du calcul automatique
                                            </h4>
                                          </div>
                                        
                                        <div className="grid grid-cols-2 gap-4">
                                          <div style={{
                                            backgroundColor: 'white',
                                            padding: '20px',
                                            borderRadius: '12px',
                                            border: '2px solid #10b981',
                                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)'
                                          }}>
                                            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                                              Sursalaire nécessaire
                                            </div>
                                            <div style={{ 
                                              fontSize: '32px', 
                                              fontWeight: 800, 
                                              color: '#10b981',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '8px'
                                            }}>
                                              <TrendingUp style={{ width: '28px', height: '28px' }} />
                                              {formatCurrency(salaryAdjustmentResult.sursalaire_needed)}
                                            </div>
                                          </div>
                                          <div style={{
                                            backgroundColor: 'white',
                                            padding: '20px',
                                            borderRadius: '12px',
                                            border: '2px solid #059669',
                                            boxShadow: '0 4px 12px rgba(5, 150, 105, 0.1)'
                                          }}>
                                            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                                              Salaire net obtenu
                                            </div>
                                            <div style={{ fontSize: '24px', fontWeight: 700, color: '#059669' }}>
                                              {formatCurrency(salaryAdjustmentResult.net_salary_achieved)}
                                            </div>
                                          </div>
                                        </div>
                                        </div>

                                        {/* Détail du calcul */}
                                        <div style={{ 
                                          backgroundColor: 'white',
                                          padding: '24px'
                                        }}>
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '16px'
                                          }}>
                                            <FileText style={{ width: '20px', height: '20px', color: '#10b981' }} />
                                            <strong style={{ fontSize: '16px', color: '#111827' }}>Détail du calcul :</strong>
                                          </div>
                                          <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: '12px',
                                            fontSize: '14px',
                                            color: '#374151'
                                          }}>
                                          <div style={{ padding: '8px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                                            • Salaire catégoriel : <strong>{formatCurrency(salaryAdjustmentResult.salary_categoriel)}</strong>
                                          </div>
                                          <div style={{ padding: '8px', backgroundColor: '#f0fdf4', borderRadius: '6px' }}>
                                            • Sursalaire calculé : <strong style={{ color: '#10b981' }}>{formatCurrency(salaryAdjustmentResult.sursalaire_needed)}</strong>
                                          </div>
                                          <div style={{ padding: '8px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                                            • Salaire brut social : <strong>{formatCurrency(salaryAdjustmentResult.salary_brut_social)}</strong>
                                          </div>
                                          <div style={{ padding: '8px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                                            • Salaire brut imposable : <strong>{formatCurrency(salaryAdjustmentResult.salary_brut_imposable)}</strong>
                                          </div>
                                          <div style={{ padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
                                            • CNPS salarié (6.3%) : <strong style={{ color: '#dc2626' }}>-{formatCurrency(salaryAdjustmentResult.cnps_salarie)}</strong>
                                          </div>
                                          <div style={{ padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
                                            • CMU salarié : <strong style={{ color: '#dc2626' }}>-{formatCurrency(salaryAdjustmentResult.cmu_salarie || 0)}</strong>
                                          </div>
                                          </div>
                                          
                                          <div style={{
                                            marginTop: '16px',
                                            padding: '16px',
                                            backgroundColor: '#f3f4f6',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            color: '#6b7280'
                                          }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                              <Info style={{ width: '16px', height: '16px', color: '#10b981' }} />
                                              <strong>Calcul de l'IS salarié :</strong>
                                            </div>
                                            <div>→ IBS (IS brut) : {formatCurrency(salaryAdjustmentResult.is_brut)}</div>
                                            <div>→ RICF ({salaryAdjustmentResult.nombre_parts} part{salaryAdjustmentResult.nombre_parts > 1 ? 's' : ''}) : -{formatCurrency(salaryAdjustmentResult.ricf_montant)}</div>
                                          </div>
                                          
                                          <div style={{
                                            marginTop: '12px',
                                            padding: '12px',
                                            backgroundColor: '#fef2f2',
                                            borderRadius: '8px'
                                          }}>
                                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#991b1b' }}>
                                              • IS salarié (IBS - RICF) : -{formatCurrency(salaryAdjustmentResult.is_net)}
                                            </div>
                                          </div>
                                          
                                          <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: '12px',
                                            marginTop: '12px',
                                            fontSize: '14px'
                                          }}>
                                            <div style={{ padding: '12px', backgroundColor: '#fef2f2', borderRadius: '8px' }}>
                                              • Total retenues : <strong style={{ color: '#dc2626' }}>-{formatCurrency(salaryAdjustmentResult.total_retenues)}</strong>
                                            </div>
                                            <div style={{ padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
                                              • Total gains non imposables : <strong style={{ color: '#10b981' }}>+{formatCurrency(salaryAdjustmentResult.total_gains)}</strong>
                                            </div>
                                          </div>
                                          
                                          <div style={{
                                            marginTop: '16px',
                                            padding: '20px',
                                            backgroundColor: '#d1fae5',
                                            borderRadius: '12px',
                                            border: '2px solid #10b981',
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            color: '#065f46',
                                            textAlign: 'center'
                                          }}>
                                            = Net à payer : {formatCurrency(salaryAdjustmentResult.net_salary_achieved)}
                                          </div>
                                        </div>

                                        {Math.abs(salaryAdjustmentResult.net_salary_achieved - salaryAdjustmentForm.target_net_salary) > 100 && (
                                          <div style={{
                                            marginTop: '16px',
                                            padding: '16px',
                                            backgroundColor: '#fffbeb',
                                            border: '2px solid #fcd34d',
                                            borderRadius: '12px',
                                            fontSize: '14px',
                                            color: '#92400e',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '12px'
                                          }}>
                                            <AlertTriangle style={{ width: '20px', height: '20px', color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
                                            <div>
                                              <strong>Écart détecté</strong> de {formatCurrency(Math.abs(salaryAdjustmentResult.net_salary_achieved - salaryAdjustmentForm.target_net_salary))} 
                                              par rapport à l'objectif. Cela peut être dû aux arrondis des calculs fiscaux.
                                            </div>
                                          </div>
                                        )}

                                        {/* Bouton pour réinitialiser ce résultat */}
                                        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                          <Button 
                                            onClick={() => setSalaryAdjustmentResult(null)}
                                            variant="outline"
                                            style={{
                                              borderColor: '#ef4444',
                                              color: '#ef4444',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '8px'
                                            }}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#fee2e2'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                          >
                                            <Trash2 style={{ width: '16px', height: '16px' }} />
                                            Réinitialiser ce résultat
                                          </Button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Bouton de soumission */}
                                  </form>
                              </div>
                            </div>
                      </div>

  );
};
